import React, { useMemo } from "react";
import { OnChange } from "react-final-form-listeners";
import { createListCollection } from "@ark-ui/react";
import { useField, useForm } from "react-final-form";

import { useTranslation } from "@/lib/i18n";
import { TextField, If } from "@/features/shared/components";
import {
  LegacyProducts,
  Converters,
  FormValues,
  Players,
  OpmetForms,
} from "@/types/widget";
import { LegacyWidgetConfiguration } from "@/lib/api";
import Select from "@/components/select/select";
import Spacing from "@/components/spacing/spacing";
import { required } from "@/features/shared/components/mui-rff-fields/validation-helpers";
import { BasicWidgetFormProps } from "@/features/widget-forms/dialogs/widget-dialog-types";

import renderSelectOptions from "./helper";

type ProductFormProps = {
  values: FormValues;
};

const DEFAULT_VALUES = {
  settings: {
    api_url: "%BASE_URL%/api",
    filename: "/data_os/subdir/filename.ext",
    file_pattern: '/data_os/subdir/filename???.ext"',
    limit: 20,
  },
};

// OnChange uses React.FC with function children, which is incompatible with React 18's
// stricter JSX checks. Cast to a plain ComponentType to satisfy the JSX component contract.
// The key problem is that OnChange's children function returns void instead of a ReactNode.
const FieldOnChange = OnChange as React.FC<{
  name: string;
  children: (value: string, previous: string) => void;
}>;

const getInitialValue = (values: FormValues, field: string) => {
  const settings = ((values?.configuration as LegacyWidgetConfiguration)?.settings ??
    {}) as Record<string, string>;

  if (field in settings) {
    return settings[field];
  }

  if (field in DEFAULT_VALUES.settings) {
    const defaultValueKey = field as keyof typeof DEFAULT_VALUES.settings;

    return DEFAULT_VALUES.settings[defaultValueKey];
  }

  return undefined;
};

const productToComponentMapper = {
  [LegacyProducts.GENERIC]: LegacyGenericProduct,
  [LegacyProducts.STATIC_FILE]: LegacyStaticFileProduct,
  [LegacyProducts.FILE]: LegacyFileProduct,
  [LegacyProducts.FILES]: LegacyFilesProduct,
  [LegacyProducts.OPMET]: LegacyOpmetProducts,
};

function useLegacyProductCollection() {
  const { t } = useTranslation();
  return useMemo(
    () =>
      createListCollection({
        items: Object.values(LegacyProducts).map((value) => ({
          label: t(`admin.widgets.legacy_products.${value}`),
          value,
        })),
      }),
    [t],
  );
}

function useConverterCollection() {
  return useMemo(
    () =>
      createListCollection({
        items: Object.values(Converters).map((value) => ({ label: value, value })),
      }),
    [],
  );
}

function usePlayerCollection() {
  return useMemo(
    () =>
      createListCollection({
        items: Object.values(Players).map((value) => ({ label: value, value })),
      }),
    [],
  );
}

function useOpmetCollection() {
  return useMemo(
    () =>
      createListCollection({
        items: Object.values(OpmetForms).map((value) => ({ label: value, value })),
      }),
    [],
  );
}

export function LegacyWidgetForm({ values }: BasicWidgetFormProps) {
  const { t } = useTranslation();
  const productCollection = useLegacyProductCollection();
  const product = (values?.configuration as LegacyWidgetConfiguration)
    ?.sub_type as LegacyProducts;
  const ProductSpecificFields = product ? productToComponentMapper[product] : () => null;
  const isUpdatingExistingWidget = !!values.id;

  const form = useForm();
  const productField = useField<string>("configuration.sub_type", {
    validate: required,
  });

  return (
    <>
      <Spacing top={4}>
        <Select
          name={productField.input.name}
          value={productField.input.value}
          onValueChange={(value) => form.change(productField.input.name, value)}
          label={t("admin.widgets.fields.legacy_product.label")}
          disabled={isUpdatingExistingWidget}
          collection={productCollection}
        />
      </Spacing>

      <FieldOnChange name={productField.input.name}>
        {(value: string) => {
          form.reset({
            ...values,
            configuration: {
              type: values?.configuration?.type ?? "legacy",
              sub_type: value,
              settings: {},
              renderer: "",
            },
          });
        }}
      </FieldOnChange>
      <If condition={!!product}>
        <ProductSpecificFields values={values} />
        <RendererTypeSelect product={product} />
      </If>
    </>
  );
}

function LegacyStaticFileProduct({ values }: ProductFormProps) {
  const { t } = useTranslation();
  const filename = getInitialValue(values, "filename");

  return (
    <>
      <TextField
        name="configuration.settings.filename"
        label={t("admin.widgets.fields.filename.label")}
        initialValue={filename}
        required
      />
      <TextField
        label={t("admin.widgets.fields.not_found_message.label")}
        name="configuration.settings.not_found_message"
      />
    </>
  );
}

function LegacyGenericProduct({ values }: ProductFormProps) {
  const { t } = useTranslation();
  const api_url = getInitialValue(values, "api_url");

  return (
    <TextField
      name="configuration.settings.api_url"
      required
      label={t("admin.widgets.fields.url.label")}
      initialValue={api_url}
    />
  );
}

function LegacyFileProduct({ values }: ProductFormProps) {
  const { t } = useTranslation();
  const converterCollection = useConverterCollection();
  const filename = getInitialValue(values, "filename");

  const form = useForm();
  const converterField = useField<string>("configuration.settings.converter", {
    validate: required,
  });

  return (
    <>
      <TextField
        name="configuration.settings.filename"
        label={t("admin.widgets.fields.filename.label")}
        initialValue={filename}
        required
      />
      <TextField
        label={t("admin.widgets.fields.max_age.label")}
        helperText={t("admin.widgets.fields.max_age.note")}
        name="configuration.settings.max_age"
      />
      <TextField
        label={t("admin.widgets.fields.not_found_message.label")}
        name="configuration.settings.not_found_message"
      />

      <Spacing top={4}>
        <Select
          collection={converterCollection}
          label={t("admin.widgets.fields.legacy_converter.label")}
          name={converterField.input.name}
          error={converterField.meta.touched && converterField.meta.error}
          value={converterField.input.value}
          onValueChange={(value) => form.change(converterField.input.name, value)}
        />
      </Spacing>
    </>
  );
}

function LegacyFilesProduct({ values }: ProductFormProps) {
  const { t } = useTranslation();
  const converterCollection = useConverterCollection();
  const playerCollection = usePlayerCollection();
  const file_pattern = getInitialValue(values, "file_pattern");
  const limit = getInitialValue(values, "limit");

  const form = useForm();
  const converterField = useField<string>("configuration.settings.converter", {
    validate: required,
  });
  const playerField = useField<string>("configuration.settings.player", {
    validate: required,
  });

  return (
    <>
      <TextField
        name="configuration.settings.file_pattern"
        helperText={t("admin.widgets.fields.file_patterns.note")}
        label={t("admin.widgets.fields.file_patterns.label")}
        initialValue={file_pattern}
        required
      />
      <TextField
        name="configuration.settings.limit"
        label={t("admin.widgets.fields.limit.label")}
        initialValue={limit}
        required
      />
      <TextField
        name="configuration.settings.max_age"
        label={t("admin.widgets.fields.max_age.label")}
        helperText={t("admin.widgets.fields.max_age.note")}
      />
      <Spacing top={4}>
        <Select
          collection={converterCollection}
          label={t("admin.widgets.fields.legacy_converter.label")}
          name={converterField.input.name}
          error={converterField.meta.touched && converterField.meta.error}
          value={converterField.input.value}
          onValueChange={(value) => form.change(converterField.input.name, value)}
        />
      </Spacing>
      <Spacing top={4}>
        <Select
          collection={playerCollection}
          label={t("admin.widgets.fields.legacy_player.label")}
          name={playerField.input.name}
          error={playerField.meta.touched && playerField.meta.error}
          value={playerField.input.value}
          onValueChange={(value) => form.change(playerField.input.name, value)}
        />
      </Spacing>
    </>
  );
}

function LegacyOpmetProducts({ values }: ProductFormProps) {
  const { t } = useTranslation();
  const opmetCollection = useOpmetCollection();
  const formType = getInitialValue(values, "form_type");

  const form = useForm();
  const opmetFormTypeField = useField<string>("configuration.settings.form_type", {
    initialValue: formType?.toString(),
    validate: required,
  });

  return (
    <>
      <Spacing top={4}>
        <Select
          collection={opmetCollection}
          label={t("admin.widgets.fields.form_type.label")}
          name="configuration.settings.form_type"
          error={opmetFormTypeField.meta.touched && opmetFormTypeField.meta.error}
          value={opmetFormTypeField.input.value}
          onValueChange={(value) => form.change(opmetFormTypeField.input.name, value)}
        />
      </Spacing>
    </>
  );
}

function RendererTypeSelect({ product }: { product: LegacyProducts }) {
  const { t } = useTranslation();
  const options = renderSelectOptions[product].map((value) => ({
    label: value,
    value,
  }));
  const collection = createListCollection({
    items: options,
  });

  const form = useForm();
  const renderField = useField<string>("configuration.renderer", {
    validate: required,
  });

  return (
    <Spacing top={4}>
      <Select
        label={t("admin.widgets.fields.renderer.label")}
        name={renderField.input.name}
        collection={collection}
        error={renderField.meta.touched && renderField.meta.error}
        value={renderField.input.value}
        onValueChange={(value) => form.change(renderField.input.name, value)}
      />
    </Spacing>
  );
}
